<script setup lang="ts">
import { computed, onMounted } from "vue";

import WIDGET_ANALYTICS_EVENTS from "~/tiw/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import { useAnalytics, useAnalyticsEventTiModel } from "~/tiw/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "~/tiw/hooks/useStoreResetter/useStoreResetter";
import useTranslate from "~/tiw/hooks/useTranslate/useTranslate";
import { useChooseDeviceStore } from "~/tiw/stores/chooseDevice/chooseDevice";
import { useEvaluationResultStore } from "~/tiw/stores/evaluationResult/evaluationResult";
import { useHowYouRateDeviceStore } from "~/tiw/stores/howYouRateDevice/howYouRateDevice";
import { useTIWStore } from "~/tiw/stores/tiw/tiw";

import HandedDevice from "../../handedDevice/handedDevice.vue";
import Modal from "../../modal/modal.vue";

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.DISCOUNT_METHOD);
});

const TIWStore = useTIWStore();
const ChooseDeviceStore = useChooseDeviceStore();
const EvaluationResultStore = useEvaluationResultStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();

function getDiscountRightNow() {
    TIWStore.IS_DISCOUNT_AFTER_TRADING = false;
    TIWStore.DISCOUNT_BY_COURIER = false;
    TIWStore.switchStep("chooseRangeDiscount");
}

function getDiscountAfterTrading() {
    TIWStore.IS_DISCOUNT_AFTER_TRADING = true;
    TIWStore.DISCOUNT_BY_COURIER = false;
    TIWStore.switchStep("successfulEvaluation");
}

function getDiscountByCourier() {
    TIWStore.DISCOUNT_BY_COURIER = true;
    TIWStore.IS_DISCOUNT_AFTER_TRADING = false;
    TIWStore.switchStep("successfulEvaluation");
}

function onEditHandler() {
    useStoreResetter();
    TIWStore.switchStep("chooseDevice");
    useAnalyticsEventTiModel();
}

const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});
</script>

<template>
    <Modal class="tiw__step__chooseRangeDiscount">
        <template #header>
            <p class="tiw__h2">
                {{ useTranslate("tiw__methodToReceiveDiscount__title") }}
            </p>
        </template>
        <HandedDevice
            :device-price="handedDevicePrice"
            :device-chain="ChooseDeviceStore.deviceChain"
            :device-state="HowYouRateDeviceStore.rate"
            :device-preview="ChooseDeviceStore.deviceImagePreview"
            :device-grade="HowYouRateDeviceStore.deviceGrade"
            :device-no-grade="EvaluationResultStore.stateOfEvaluation === 'UNKNOWN'"
            :device-terminated="EvaluationResultStore.stateOfEvaluation === 'TERMINATED'"
            :on-edit="onEditHandler"
        />
        <div class="tiw__step__chooseRangeDiscount__getDiscount">
            <button
                class="tiw__step__chooseRangeDiscount__getDiscount__item"
                @click="getDiscountRightNow"
            >
                <svg
                    width="60"
                    height="48"
                    viewBox="0 0 60 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M27.2 46.3995H25.8C15.2748 46.3995 10.0136 46.3995 6.49959 43.6275C5.94116 43.1852 5.42215 42.6952 4.94839 42.1631C2 38.8591 2 33.9032 2 23.9996C2 14.096 2 9.1428 4.94839 5.836C5.42066 5.30587 5.93773 4.81867 6.49959 4.37441C10.0136 1.59961 15.2748 1.59961 25.8 1.59961H34.1999C44.7251 1.59961 49.9863 1.59961 53.4975 4.3716C54.0612 4.8196 54.5792 5.30774 55.0515 5.836C57.7087 8.8152 57.9719 13.13 57.9999 21.1996M2.10689 15.6733H57.8947"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M35.5996 40.7996C35.5996 40.7996 38.3996 40.7996 41.1996 46.3996C41.1996 46.3996 50.0952 32.3996 57.9996 29.5996"
                        stroke="#6959CB"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <div>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__title">
                        {{ useTranslate("tiw__methodToReceiveDiscount__getOnline__title") }}
                    </p>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__text">
                        {{ useTranslate("tiw__methodToReceiveDiscount__getOnline__text") }}
                    </p>
                </div>
            </button>
            <button
                class="tiw__step__chooseRangeDiscount__getDiscount__item"
                @click="getDiscountAfterTrading"
            >
                <svg
                    width="58"
                    height="64"
                    viewBox="0 0 58 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M56 56C54.071 52.139 50 47.072 50 42.584C50 37.487 51.101 31.202 48.734 26.465C47.732 24.467 47 22.442 47 20.165V9.29C47 8.94839 46.8645 8.62073 46.6232 8.37889C46.382 8.13706 46.0546 8.0008 45.713 8C44.7 7.99961 43.6969 8.19884 42.7609 8.58631C41.8249 8.97379 40.9745 9.54191 40.2582 10.2582C39.5419 10.9745 38.9738 11.8249 38.5863 12.7609C38.1988 13.6969 37.9996 14.7 38 15.713M17 50L27.905 56.816C28.625 57.266 29.243 57.866 29.717 58.574L32 62"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M8.08105 41C8.24605 44.291 8.73505 46.34 10.1961 47.804C12.3921 50 15.9261 50 23.0001 50C30.0741 50 33.6051 50 35.8041 47.804C38.0001 45.605 38.0001 42.071 38.0001 35V17C38.0001 9.929 38.0001 6.392 35.8041 4.196C33.6051 2 30.0711 2 23.0001 2C15.9291 2 12.3921 2 10.1961 4.196C8.73505 5.657 8.24605 7.709 8.08105 11"
                        stroke="#6959CB"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M5.75 18.5H10.25C11.2446 18.5 12.1984 18.1049 12.9017 17.4017C13.6049 16.6984 14 15.7446 14 14.75C14 13.7554 13.6049 12.8016 12.9017 12.0983C12.1984 11.3951 11.2446 11 10.25 11H5.75C4.75544 11 3.80161 11.3951 3.09835 12.0983C2.39509 12.8016 2 13.7554 2 14.75C2 15.7446 2.39509 16.6984 3.09835 17.4017C3.80161 18.1049 4.75544 18.5 5.75 18.5ZM5.75 18.5H13.25C14.2446 18.5 15.1984 18.8951 15.9017 19.5983C16.6049 20.3016 17 21.2554 17 22.25C17 23.2446 16.6049 24.1984 15.9017 24.9017C15.1984 25.6049 14.2446 26 13.25 26H5.75M5.75 18.5C4.75544 18.5 3.80161 18.8951 3.09835 19.5983C2.39509 20.3016 2 21.2554 2 22.25C2 23.2446 2.39509 24.1984 3.09835 24.9017C3.80161 25.6049 4.75544 26 5.75 26M5.75 26H10.25C11.2446 26 12.1984 26.3951 12.9017 27.0984C13.6049 27.8016 14 28.7554 14 29.75C14 30.7446 13.6049 31.6984 12.9017 32.4016C12.1984 33.1049 11.2446 33.5 10.25 33.5H5.75M5.75 26C4.75544 26 3.80161 26.3951 3.09835 27.0984C2.39509 27.8016 2 28.7554 2 29.75C2 30.7446 2.39509 31.6984 3.09835 32.4016C3.80161 33.1049 4.75544 33.5 5.75 33.5M5.75 33.5H8.75C9.74456 33.5 10.6984 33.8951 11.4017 34.5984C12.1049 35.3016 12.5 36.2554 12.5 37.25C12.5 38.2446 12.1049 39.1984 11.4017 39.9016C10.6984 40.6049 9.74456 41 8.75 41H5.75C4.75544 41 3.80161 40.6049 3.09835 39.9016C2.39509 39.1984 2 38.2446 2 37.25C2 36.2554 2.39509 35.3016 3.09835 34.5984C3.80161 33.8951 4.75544 33.5 5.75 33.5Z"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M23 41H23.027"
                        stroke="#6959CB"
                        stroke-width="3.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <div>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__title">
                        {{ useTranslate("tiw__methodGetDiscountAfter__title") }}
                    </p>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__text">
                        {{ useTranslate("tiw__methodGetDiscountAfter__text") }}
                    </p>
                </div>
            </button>
            <button
                class="tiw__step__chooseRangeDiscount__getDiscount__item"
                @click="getDiscountByCourier"
            >
                <svg
                    width="59"
                    height="48"
                    viewBox="0 0 59 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M43.5004 46.2996C46.5932 46.2996 49.1004 43.7924 49.1004 40.6996C49.1004 37.6068 46.5932 35.0996 43.5004 35.0996C40.4076 35.0996 37.9004 37.6068 37.9004 40.6996C37.9004 43.7924 40.4076 46.2996 43.5004 46.2996Z"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M15.5004 46.2996C18.5932 46.2996 21.1004 43.7924 21.1004 40.6996C21.1004 37.6068 18.5932 35.0996 15.5004 35.0996C12.4076 35.0996 9.90039 37.6068 9.90039 40.6996C9.90039 43.7924 12.4076 46.2996 15.5004 46.2996Z"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M9.9 40.6216C6.8284 40.4704 4.916 40.014 3.5496 38.6504C2.1832 37.2868 1.7296 35.3716 1.5784 32.3M21.1 40.7H37.9M49.1 40.6216C52.1716 40.4704 54.084 40.014 55.4504 38.6504C57.5 36.598 57.5 33.2996 57.5 26.7V21.1H44.34C42.254 21.1 41.2124 21.1 40.3696 20.8256C39.5292 20.5525 38.7653 20.0844 38.1405 19.4595C37.5156 18.8347 37.0475 18.0708 36.7744 17.2304C36.5 16.3876 36.5 15.346 36.5 13.26C36.5 10.1324 36.5 8.57 36.0884 7.3044C35.6788 6.04377 34.9766 4.89798 34.0393 3.9607C33.102 3.02341 31.9562 2.32125 30.6956 1.9116C29.43 1.5 27.8676 1.5 24.74 1.5H1.5"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M1.5 12.7002H18.3M1.5 21.1002H12.7"
                        stroke="#6959CB"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M36.5 7.09961H41.5988C45.6756 7.09961 47.7112 7.09961 49.3688 8.09081C51.0292 9.07921 51.9952 10.874 53.9272 14.4636L57.5 21.0996"
                        stroke="#808D9A"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>

                <div>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__title">
                        {{ useTranslate("tiw__chooseHowToReceiveDiscount__pickup__title") }}
                    </p>
                    <p class="tiw__step__chooseRangeDiscount__getDiscount__item__text">
                        {{ useTranslate("tiw__chooseHowToReceiveDiscount__pickup__text") }}
                    </p>
                </div>
            </button>
        </div>
    </Modal>
</template>

<style lang="scss" src="./chooseHowToReceiveDiscount.scss"></style>
